/**
 * AGROMX Frontend - Aviso de novedades y versión en el pie (C-337)
 *
 * - pintarVersion(): «AgroMX v1.1» en el pie de pantalla.
 * - mostrarNovedadesPendientes(): al iniciar sesión, si hay novedades de su
 *   rol que el usuario no ha visto, abre la ventana «Novedades v1.x». Sólo se
 *   cierra con «Entendido», que lo registra en el servidor (por usuario y
 *   versión) y ya no vuelve a salir.
 * Nada de esto debe impedir trabajar: si falla, se registra en consola y ya.
 */

import novedadesService from '../services/novedades.service.js';
import modal from './modal.js';
import toast from './toast.js';

const escapar = (s) => String(s ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/** Texto plano del JSON → párrafos (cada salto de línea es un párrafo). */
export const parrafos = (texto) => String(texto || '')
  .split('\n')
  .map(l => l.trim())
  .filter(Boolean)
  .map(l => `<p class="novedad-parrafo">${escapar(l)}</p>`)
  .join('');

export const fechaLarga = (iso) => {
  if (!iso) return '';
  const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  const meses = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
    'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  return `${d} de ${meses[m - 1]} de ${a}`;
};

export const etiquetaBorrador = '<span class="novedad-borrador">BORRADOR · los usuarios de producción no lo ven</span>';

const htmlVersiones = (versiones) => versiones.map(v => `
  <section class="novedad-version" data-version="${escapar(v.version)}">
    ${versiones.length > 1 ? `<h4 class="novedad-version-titulo">${escapar(v.version)} · ${escapar(fechaLarga(v.fecha))}</h4>` : ''}
    ${v.borrador ? etiquetaBorrador : ''}
    ${v.novedades.map(n => `
      <article class="novedad-item">
        <h5 class="novedad-item-titulo">${escapar(n.titulo)}</h5>
        ${parrafos(n.texto)}
      </article>
    `).join('')}
  </section>
`).join('');

export async function pintarVersion() {
  const pie = document.getElementById('appFooter');
  if (!pie) return;
  try {
    const { version } = await novedadesService.version();
    if (!version) return;
    pie.innerHTML = `AgroMX <span class="app-footer-version" id="appVersion">${escapar(version)}</span>`;
    pie.style.display = '';
  } catch (err) {
    console.warn('[Novedades] Sin versión:', err?.message || err);
  }
}

export async function mostrarNovedadesPendientes() {
  let datos;
  try {
    datos = await novedadesService.pendientes();
  } catch (err) {
    console.warn('[Novedades] No se pudieron consultar:', err?.message || err);
    return;
  }
  const versiones = datos?.versiones || [];
  if (!versiones.length) return;

  // La más nueva da el número del título; si se juntaron varias, van todas abajo.
  const ultima = versiones[versiones.length - 1].version;
  const cuerpo = `
    <div class="novedades-aviso" id="novedadesAviso">
      <p class="novedades-intro">Esto cambió en AgroMX para tu trabajo:</p>
      ${htmlVersiones(versiones)}
      <p class="novedades-pie">Puedes volver a leerlo en el menú, en «¿Qué hay de nuevo?».</p>
    </div>
  `;

  modal.open(`✨ Novedades ${escapar(ultima)}`, cuerpo, [{
    text: 'Entendido',
    class: 'btn-primary',
    onClick: async () => {
      try {
        await novedadesService.marcarVistas(versiones.map(v => v.version));
      } catch (err) {
        // Si no se pudo registrar, le volverá a salir en el siguiente inicio de sesión.
        console.warn('[Novedades] No se registró el visto:', err?.message || err);
        toast.warning('No se pudo guardar que ya lo leíste; te lo volveremos a mostrar.');
      }
    }
  }], { dismissable: false });
}
